import { useRef, useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import SmartImg from '../ui/SmartImg';
import { IconButton, TrashIcon, StarIcon } from '../ui/icons';
import { PHOTO_CATEGORIES, categoryClass, categoryLabel } from '../../lib/photoCategories';
import { fmtDate, fmtStamp } from '../../lib/dates';
import {
  prepareImageUpload,
  isAcceptableImage,
  IMAGE_ACCEPT as ACCEPT,
  MAX_INPUT_BYTES,
} from '../../lib/compressImage';

// Accept list, size caps and the compress-unless-GIF rule now live in
// lib/compressImage.js so the cover-photo picker in HorseForm behaves
// identically. It previously uploaded raw files and rejected iPhone HEICs.
//
// Photos carry a category, a date they were taken, and optionally the event
// they belong to — a vet visit, an incident, or a note. That's what turns a
// gallery into a record: five pictures of the same leg over three weeks, filed
// under Injury and hanging off the vet visit that assessed it, read in the
// order the injury actually happened rather than the order someone got round
// to uploading them.
//
// Filing is per batch, because photographing one leg five times is the real
// job and five unrelated photos in one go is not. It used to be a row of
// dropdowns in the card header, standing there whether you were uploading or
// not — which read as the state of the card, or as a filter, rather than as
// settings for a file you had not chosen yet. Somebody reasonably tried to use
// them to recategorize a photo already on the page, and nothing happened.
//
// So the same three fields now appear after the files are picked, in a sheet
// that names the photos it is about, and changing a photo AFTER the fact is
// its own thing: open it and edit the fields under it.

export default function HorsePhotoGallery({
  horseId,
  canUpload = true,
  canSetCover = true,
  // When the gallery is rendered inside a vet record or a note, uploads attach
  // to it automatically and the picker is hidden — you're already in the thing.
  attachTo = null,
}) {
  const qc = useQueryClient();
  const inputRef = useRef(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [lightbox, setLightbox] = useState(null); // index into photos, or null
  const [filter, setFilter] = useState(''); // '' = all, 'none' = uncategorized
  // `attach` is a "kind:id" string so one <select> can offer both records and
  // notes without a second control for which kind it is.
  //
  // Taken on defaults to today: the overwhelming case is somebody standing in
  // the barn photographing a leg on their phone. Backdating a picture from last
  // week is the exception, and it's still one tap away — whereas an empty field
  // meant every photo taken today was filed with no date at all unless the
  // person remembered to fill it in.
  const [meta, setMeta] = useState({ category: '', takenOn: todayLocal(), attach: '' });
  // Files chosen and waiting to be filed. Holding them here rather than
  // uploading straight away is what lets the sheet name what it's about.
  const [pending, setPending] = useState(null);
  // The photo queued for deletion, held until it's confirmed.
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['horse-photos', horseId, filter],
    queryFn: () =>
      api.get(`/horses/${horseId}/photos${filter ? `?category=${filter}` : ''}`),
    // Counts come from the unfiltered set, so keeping the previous response
    // means the filter chips don't flicker away mid-fetch. v5 spelling —
    // keepPreviousData was removed and is silently ignored if you pass it.
    placeholderData: (prev) => prev,
  });

  const photos = data?.photos ?? [];
  const counts = data?.counts ?? {};
  const attachables = data?.attachables ?? [];

  // After any change, refresh the gallery and the horse record (its cover
  // lives on horses.photo_url, which the page header renders).
  const refresh = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['horse-photos', horseId] });
    qc.invalidateQueries({ queryKey: ['horse', horseId] });
  }, [qc, horseId]);

  function onPick(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-selecting the same file later
    if (files.length === 0) return;

    const rejected = [];
    const valid = files.filter((f) => {
      if (!isAcceptableImage(f)) return rejected.push(`${f.name} (unsupported)`), false;
      if (f.size > MAX_INPUT_BYTES) return rejected.push(`${f.name} (over 50 MB)`), false;
      return true;
    });
    setError(rejected.length ? `Skipped: ${rejected.join(', ')}` : '');
    if (valid.length === 0) return;

    // Every batch starts from today with no category, rather than inheriting
    // whatever the last one used — a stale category silently applied to a new
    // set of photos is worse than no category at all.
    setMeta({ category: '', takenOn: todayLocal(), attach: '' });
    setPending(valid);
  }

  async function upload(valid) {
    setPending(null);
    setBusy(true);
    setProgress({ done: 0, total: valid.length });
    const failed = [];

    for (let i = 0; i < valid.length; i++) {
      const file = valid[i];
      try {
        // Compresses unless it's a GIF (which would lose its animation), and
        // throws file_too_large if the result still exceeds the server cap.
        const toUpload = await prepareImageUpload(file);
        await api.upload(`/horses/${horseId}/photos`, toUpload, {
          category: meta.category,
          takenOn: meta.takenOn,
          ...attachFields(attachTo ? `${attachTo.kind}:${attachTo.id}` : meta.attach),
        });
      } catch (err) {
        failed.push(
          err?.code === 'file_too_large' ? `${file.name} (still over 8 MB)` : file.name,
        );
      }
      setProgress({ done: i + 1, total: valid.length });
    }

    setBusy(false);
    if (failed.length) {
      setError((prev) =>
        [prev, `Failed: ${failed.join(', ')}`].filter(Boolean).join(' · ')
      );
    }
    refresh();
  }

  const setCover = useMutation({
    mutationFn: (photoId) => api.post(`/horses/${horseId}/photos/${photoId}/cover`),
    onSuccess: refresh,
    onError: (err) => setError(err?.body?.error || err.message || 'Could not set cover'),
  });


  const setMetaOn = useMutation({
    mutationFn: ({ photoId, ...fields }) =>
      api.patch(`/horses/${horseId}/photos/${photoId}`, fields),
    onSuccess: refresh,
    onError: (err) => setError(err?.body?.error || err.message || 'Could not save that'),
  });

  const remove = useMutation({
    mutationFn: (photoId) => api.del(`/horses/${horseId}/photos/${photoId}`),
    onSuccess: () => {
      setLightbox(null);
      refresh();
    },
    onError: (err) => setError(err?.body?.error || err.message || 'Could not delete photo'),
  });

  // v4/v5-safe pending flags (v5 uses isPending, v4 used isLoading).
  const settingCover = setCover.isPending || setCover.isLoading;
  const deleting = remove.isPending || remove.isLoading;

  return (
    <section className="card p-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium">Photos</h2>
        {canUpload && (
          <>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={onPick}
            />
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              {busy ? `Processing ${progress.done}/${progress.total}…` : 'Add Photos'}
            </button>
          </>
        )}
      </div>

      {/* Filter chips. Counts are over every photo on the horse, so a chip
          can't disappear just because another filter is active. */}
      {Object.keys(counts).length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          <FilterChip active={filter === ''} onClick={() => setFilter('')}>
            All {photosTotal(counts)}
          </FilterChip>
          {PHOTO_CATEGORIES.filter((c) => counts[c.token]).map((c) => (
            <FilterChip
              key={c.token}
              active={filter === c.token}
              onClick={() => setFilter(filter === c.token ? '' : c.token)}
            >
              {c.label} {counts[c.token]}
            </FilterChip>
          ))}
          {counts.none > 0 && (
            <FilterChip
              active={filter === 'none'}
              onClick={() => setFilter(filter === 'none' ? '' : 'none')}
            >
              Uncategorized {counts.none}
            </FilterChip>
          )}
        </div>
      )}

      {pending && (
        <FilePhotosSheet
          files={pending}
          meta={meta}
          setMeta={setMeta}
          attachTo={attachTo}
          attachables={attachables}
          onCancel={() => setPending(null)}
          onConfirm={() => upload(pending)}
        />
      )}

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {isLoading ? (
        <p className="text-sm text-ink-400">Loading…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Couldn’t load photos.</p>
      ) : photos.length === 0 ? (
        <p className="text-sm text-ink-400">
          {filter ? 'No photos in that category.' : 'No additional photos yet.'}
        </p>
      ) : (
        <ul className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {photos.map((p, i) => (
            <li key={p.id}>
              {/* The picture, then its actions under it — rather than the
                  actions living inside the viewer, where you had to open a
                  photo to find out you could delete it. Icons, like every
                  other row action in the app. */}
              <div className="relative aspect-square">
                <button
                  type="button"
                  onClick={() => setLightbox(i)}
                  className="block w-full h-full"
                >
                  <SmartImg
                    src={p.url}
                    width={160}
                    alt={p.caption || ''}
                    className="w-full h-full object-cover rounded-md"
                  />
                </button>
                {p.category && (
                  <span
                    className={
                      'absolute top-1 left-1 text-[10px] font-medium px-1.5 py-0.5 rounded ' +
                      categoryClass(p.category)
                    }
                  >
                    {categoryLabel(p.category)}
                  </span>
                )}
                {(p.caption || p.takenOn) && (
                  <span className="absolute inset-x-0 bottom-0 px-1.5 py-1 text-[11px] leading-tight text-white bg-gradient-to-t from-black/70 to-transparent rounded-b-md">
                    {p.takenOn && (
                      <span className="block text-[10px] text-white/80">{shortDate(p.takenOn)}</span>
                    )}
                    {p.caption && <span className="block line-clamp-2">{p.caption}</span>}
                  </span>
                )}
              </div>

              <div className="flex items-center justify-end gap-0.5 pt-0.5 -mr-1">
                {p.isCover ? (
                  // Already the cover: the star stays, filled, as the answer to
                  // "which one is it" — there is nothing to press.
                  <span
                    title="Cover photo"
                    aria-label="Cover photo"
                    className="inline-flex items-center justify-center w-8 h-8 text-hunter"
                  >
                    <StarIcon filled />
                  </span>
                ) : canSetCover ? (
                  <IconButton
                    icon={StarIcon}
                    label="Use as cover photo"
                    disabled={settingCover}
                    onClick={() => setCover.mutate(p.id)}
                  />
                ) : null}
                {canUpload && (
                  <IconButton
                    icon={TrashIcon}
                    tone="danger"
                    label="Delete photo"
                    disabled={deleting}
                    onClick={() => setConfirmDelete(p)}
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {lightbox != null && photos[lightbox] && (
        <PhotoModal
          photo={photos[lightbox]}
          hasPrev={lightbox > 0}
          hasNext={lightbox < photos.length - 1}
          onPrev={() => setLightbox((n) => Math.max(0, n - 1))}
          onNext={() => setLightbox((n) => Math.min(photos.length - 1, n + 1))}
          onClose={() => setLightbox(null)}
          canCaption={canUpload}
          canEditMeta={canUpload}
          attachables={attachables}
          saving={setMetaOn.isPending}
          // One PATCH with only the changed fields, then close — Save means
          // the edit is over, not that a panel stays open behind it.
          onSave={(fields) =>
            setMetaOn.mutate(
              { photoId: photos[lightbox].id, ...fields },
              { onSuccess: () => setLightbox(null) },
            )
          }
        />
      )}

      {confirmDelete && (
        <ConfirmDeletePhoto
          photo={confirmDelete}
          deleting={deleting}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() =>
            remove.mutate(confirmDelete.id, { onSettled: () => setConfirmDelete(null) })
          }
        />
      )}
    </section>
  );
}

/**
 * Deleting a photo.
 *
 * A trash icon under a thumbnail is the same one-tap-destroys-a-thing shape
 * that came off the time-off list, so it asks first — and shows the picture,
 * because in a grid of similar photos the only reliable way to know which one
 * you have is to see it.
 */
function ConfirmDeletePhoto({ photo, deleting, onCancel, onConfirm }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white w-full sm:max-w-sm rounded-t-xl sm:rounded-xl p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-medium text-lg">Delete this photo?</h2>
        <div className="flex items-start gap-3 mt-3">
          <SmartImg
            src={photo.url}
            width={96}
            alt={photo.caption || ''}
            className="w-20 h-20 object-cover rounded-md shrink-0"
          />
          <p className="text-sm text-ink-500">
            It goes to the archive and can be restored there. Deleting it from the archive is
            what removes it for good.
          </p>
        </div>
        <div className="flex items-center gap-2 mt-4">
          <button
            type="button"
            className="btn-secondary text-rose-700 border-rose-200 hover:border-rose-400"
            disabled={deleting}
            onClick={onConfirm}
          >
            {deleting ? 'Deleting…' : 'Delete It'}
          </button>
          <button type="button" className="btn-secondary" disabled={deleting} onClick={onCancel}>
            Never mind
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * File the photos you just picked, before they go up.
 *
 * These three fields used to sit in the card header at all times, which made
 * them look like the card's own state — or a filter — rather than settings for
 * files nobody had chosen yet. Here they name the batch they apply to, and
 * they cannot be mistaken for a way to change a photo that already exists.
 *
 * Nothing here is required. "Upload" with the defaults files everything as
 * uncategorized and dated today, which is the right outcome for somebody who
 * just wants the picture on the record and will sort it out later.
 */
function FilePhotosSheet({ files, meta, setMeta, attachTo, attachables, onCancel, onConfirm }) {
  const count = files.length;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl p-5 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-medium text-lg">
          {count === 1 ? 'Add this photo' : `Add these ${count} photos`}
        </h2>
        <p className="text-sm text-ink-500 mt-1">
          Everything here applies to {count === 1 ? 'it' : 'all of them'}, and can be changed
          afterwards by opening a photo.
        </p>

        {/* The filenames, so it's obvious which photos this is about — the
            complaint that started this was not knowing what the fields
            applied to. */}
        <ul className="mt-3 mb-4 text-xs text-ink-500 max-h-24 overflow-y-auto">
          {files.slice(0, 8).map((f, i) => (
            <li key={i} className="truncate">{f.name}</li>
          ))}
          {count > 8 && <li className="text-ink-400">and {count - 8} more</li>}
        </ul>

        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="sheet-cat">
                What is it of?
              </label>
              <select
                id="sheet-cat"
                className="input"
                value={meta.category}
                onChange={(e) => setMeta((m) => ({ ...m, category: e.target.value }))}
              >
                <option value="">No category</option>
                {PHOTO_CATEGORIES.map((c) => (
                  <option key={c.token} value={c.token}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="sheet-date">
                Taken on
              </label>
              <input
                id="sheet-date"
                type="date"
                className="input"
                value={meta.takenOn}
                onChange={(e) => setMeta((m) => ({ ...m, takenOn: e.target.value }))}
              />
            </div>
          </div>

          {attachTo ? (
            <p className="text-sm text-ink-500">
              Attaching to <span className="font-medium text-ink-800">{attachTo.label}</span>.
            </p>
          ) : attachables.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="sheet-attach">
                Attach to
              </label>
              <select
                id="sheet-attach"
                className="input"
                value={meta.attach}
                onChange={(e) => setMeta((m) => ({ ...m, attach: e.target.value }))}
              >
                <option value="">Nothing</option>
                {attachables.map((a) => (
                  <option key={`${a.kind}:${a.id}`} value={`${a.kind}:${a.id}`}>
                    {attachableLabel(a)}
                  </option>
                ))}
              </select>
              <p className="text-xs text-ink-400 mt-1">
                A vet visit, an incident or a note — so the pictures sit with the thing they
                are evidence of.
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex flex-wrap items-center gap-2 mt-5">
          <button type="button" className="btn-primary" onClick={onConfirm}>
            {count === 1 ? 'Upload Photo' : `Upload ${count} Photos`}
          </button>
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * One photo, in a modal you can see the edges of.
 *
 * It used to be a full-bleed overlay: the image floated in the middle of the
 * screen and the caption, the filing fields and the buttons spread across the
 * whole viewport under it, each on its own band. Nothing said where the thing
 * you were looking at ended, and on a wide screen the fields sat a foot apart
 * from the picture they described.
 *
 * Edits are staged rather than saved as you type. That is what Save and Cancel
 * mean: change the category, change your mind, close it, nothing happened.
 * Cancel is the reason the modal exists, and auto-save is the reason it
 * couldn't have one.
 *
 * Delete and Set as Cover are not here — they're icons under the thumbnail in
 * the grid, where you don't have to open a photo to find out you can manage it.
 */
function PhotoModal({
  photo,
  canCaption,
  canEditMeta,
  attachables = [],
  saving,
  onSave,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose,
}) {
  const attachOf = (p) => (p.attachedTo ? `${p.attachedTo.kind}:${p.attachedTo.id}` : '');
  const initial = {
    caption: photo.caption ?? '',
    category: photo.category ?? '',
    takenOn: photo.takenOn ?? '',
    attach: attachOf(photo),
  };
  const [draft, setDraft] = useState(initial);

  // Moving to another photo starts a new edit, so the fields reload. Anything
  // typed and not saved is gone — which is why the footer says so while it's
  // still there to lose.
  useEffect(() => {
    setDraft({
      caption: photo.caption ?? '',
      category: photo.category ?? '',
      takenOn: photo.takenOn ?? '',
      attach: attachOf(photo),
    });
  }, [photo.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty =
    draft.caption !== initial.caption ||
    draft.category !== initial.category ||
    draft.takenOn !== initial.takenOn ||
    draft.attach !== initial.attach;

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && hasPrev) onPrev();
      else if (e.key === 'ArrowRight' && hasNext) onNext();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [hasPrev, hasNext, onPrev, onNext, onClose]);

  /** Only what actually changed, so two people editing different fields don't clobber each other. */
  function save() {
    const fields = {};
    if (draft.caption !== initial.caption) fields.caption = draft.caption;
    if (draft.category !== initial.category) fields.category = draft.category;
    if (draft.takenOn !== initial.takenOn) fields.takenOn = draft.takenOn;
    if (draft.attach !== initial.attach) Object.assign(fields, attachFields(draft.attach));
    onSave(fields);
  }

  const set = (k) => (e) => setDraft((d) => ({ ...d, [k]: e.target.value }));

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-3xl rounded-t-xl sm:rounded-xl flex flex-col max-h-[92vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Photo"
      >
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-ink-100">
          <h2 className="font-medium">
            {photo.takenOn ? `Taken ${shortDate(photo.takenOn)}` : 'Photo'}
          </h2>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={onPrev}
              disabled={!hasPrev}
              aria-label="Previous photo"
              className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 disabled:opacity-30 text-xl leading-none"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={!hasNext}
              aria-label="Next photo"
              className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 disabled:opacity-30 text-xl leading-none"
            >
              ›
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none ml-1"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* A dark ground behind the picture, so a photo with white in it
              still has an edge. */}
          <div className="bg-ink-900 flex items-center justify-center p-3">
            <img
              src={photo.url}
              alt={photo.caption || ''}
              className="max-h-[46vh] max-w-full object-contain rounded"
            />
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pm-caption">
                Caption
              </label>
              <input
                id="pm-caption"
                type="text"
                className="input"
                placeholder="What does this show?"
                maxLength={300}
                disabled={!canCaption || saving}
                value={draft.caption}
                onChange={set('caption')}
              />
            </div>

            {canEditMeta && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pm-cat">
                    What is it of?
                  </label>
                  <select id="pm-cat" className="input" disabled={saving} value={draft.category} onChange={set('category')}>
                    <option value="">No category</option>
                    {PHOTO_CATEGORIES.map((c) => (
                      <option key={c.token} value={c.token}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pm-date">
                    Taken on
                  </label>
                  <input id="pm-date" type="date" className="input" disabled={saving} value={draft.takenOn} onChange={set('takenOn')} />
                </div>
                {attachables.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="pm-attach">
                      Attached to
                    </label>
                    <select id="pm-attach" className="input" disabled={saving} value={draft.attach} onChange={set('attach')}>
                      <option value="">Nothing</option>
                      {attachables.map((a) => (
                        <option key={`${a.kind}:${a.id}`} value={`${a.kind}:${a.id}`}>
                          {attachableLabel(a)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-ink-400">
              Uploaded {uploadedAt(photo.createdAt)}
              {photo.uploadedBy ? ` by ${photo.uploadedBy}` : ''}
              {photo.isCover ? ' · this is the cover photo' : ''}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 px-5 py-3 border-t border-ink-100">
          <button type="button" className="btn-primary" disabled={saving || !dirty} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="btn-secondary" disabled={saving} onClick={onClose}>
            Cancel
          </button>
          {dirty && !saving && (
            <span className="text-xs text-ink-400 ml-auto">Unsaved changes</span>
          )}
        </div>
      </div>
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'text-xs px-2 py-1 rounded-full border transition-colors ' +
        (active
          ? 'bg-ink-800 text-white border-ink-800'
          : 'bg-white text-ink-600 border-ink-200 hover:bg-ink-50')
      }
    >
      {children}
    </button>
  );
}

/**
 * What this photo is of, when it was taken, and what it belongs to.
 *
 * Also where the upload timestamp is shown. It's the provenance line: if two
 * people disagree about how a leg looked on the 12th, the answer is who
 * uploaded what, and when.
 */

function photosTotal(counts) {
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

/** "2026-09-01" -> "Sep 1". Was "1 Sep" — day first, which is not the order
 *  the rest of the app reads in. */
const shortDate = (iso) => fmtDate(iso, 'short');

/** created_at is a unix timestamp; show date AND time — that was the ask. */
function uploadedAt(unix) {
  if (!unix) return 'unknown';
  return fmtStamp(unix, 'medium', true);
}

/** "record:abc" -> the pair of fields the API expects. Empty clears both. */
function attachFields(value) {
  const [kind, id] = String(value || '').split(':');
  return {
    healthRecordId: kind === 'record' && id ? id : '',
    noteId: kind === 'note' && id ? id : '',
  };
}

function attachableLabel(a) {
  const prefix = a.kind === 'record' ? '' : 'Note: ';
  return `${prefix}${a.label}${a.date ? ` · ${shortDate(a.date)}` : ''}`;
}

/**
 * Today as YYYY-MM-DD in the viewer's own timezone.
 *
 * Deliberately the device's date, not the barn's: whoever is holding the phone
 * is standing at the barn, and toISOString() would hand back the UTC date,
 * which after 8pm Eastern is already tomorrow.
 */
function todayLocal() {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}
